import { usePortalNotifications } from '@/context/Notifications';
import {
  HeaderMenus,
  toggleGlobalHeaderOption,
} from '@/features/GlobalHeaderContainer/GlobalHeader/GlobalHeader.events';
import Image from '@/features/shared/Image';
import { MuiButtonBase, MuiCard, MuiTypography } from '@/features/shared/material-ui';
import RichText from '@/features/shared/RichText';
import { Theme } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import { replaceInText } from '@/utils/text';
import clsx from 'clsx';
import { get } from 'lodash/fp';
import React, { useCallback } from 'react';
import { Notification } from '../Notifications.interfaces';
import { DateRelative, timeDifference } from '../Notifications.utils';
import useNotificationCardStyles from './NotificationCard.styles';

const NotificationCard = ({
  className,
  isUnread = false,
  notification,
  notificationsLabels = {},
  onClickNotification,
}: {
  notification: Notification;
  className?: string;
  isUnread: boolean;
  notificationsLabels: any;
  onClickNotification?: () => void;
}) => {
  const classes = useNotificationCardStyles();
  const linkProps = mapLinkToProps(notification.link);
  const { markAsRead } = usePortalNotifications();

  const getRelativeDate = useCallback(
    (relativeDateConfig: DateRelative) => {
      const templateMatch = get(
        `relative_time_${relativeDateConfig.unit}_label`,
        notificationsLabels,
      );
      if (!templateMatch) return 'Some time ago';
      return replaceInText([relativeDateConfig.value.toString()], templateMatch);
    },
    [notificationsLabels],
  );

  const handleNotificationClick = useCallback(() => {
    toggleGlobalHeaderOption(HeaderMenus.NotificationsSection);
    onClickNotification?.();
    if (!isUnread) return;
    markAsRead(notification.id).then();
  }, [isUnread, notification, markAsRead, onClickNotification]);

  return (
    <MuiCard elevation={0} className={clsx(className, classes.root)}>
      <MuiButtonBase
        className={classes.buttonBase}
        {...linkProps}
        onClick={handleNotificationClick}
      >
        <div>
          <div className="content">
            {isUnread && <span className={classes.unreadBadge} />}
            <MuiTypography variant="h3" component="h3" className={classes.title}>
              {notification?.heading}
            </MuiTypography>
            {notification?.image && (
              <div className={classes.imageContainer}>
                <Image image={notification?.image} base="original" />
              </div>
            )}
            <RichText
              theme={Theme.dark}
              content={notification.detailContent}
              className={classes.description}
            />
            <MuiTypography variant="body1" component="p" className={classes.time}>
              {getRelativeDate(timeDifference(new Date(), new Date(notification.publicationDate)))}
            </MuiTypography>
          </div>
        </div>
      </MuiButtonBase>
    </MuiCard>
  );
};

export default NotificationCard;
